import React, { useCallback } from 'react';
import { RootState } from '@/store/store';
import { BaseButton } from '@/components/uiKit/Button';
import { BaseCard } from '@/components/uiKit/Cards';
import { useFetchResource } from '@/hooks';
import { SkipBack, SkipForward, VideoIcon, X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { DMVideoPlayer } from '@/components/shared/media/DMVideoPlayer';
import dataMindLogo from '@/assets/images/data-mind-logo.svg';
import { setVisualAIActiveItemIndex } from '@/store/slices/assetStudioSlice';
import { cn } from '@/lib/utils';

interface VideoPlayerProps {
  resource_id: string;
  onClose: () => void;
  className?: string;
}

export const EvidenceVideoPlayer = ({ resource_id, onClose, className }: VideoPlayerProps) => {
  const dispatch = useDispatch();
  const { resource, isLoadingResource, isError, retry } = useFetchResource(resource_id);
  const { visualAIActiveItemIndex, visualAIPlaylistItems } = useSelector((state: RootState) => state.assetsStudio);

  const changeVideoIndex = useCallback(
    (direction: number) => {
      if (visualAIActiveItemIndex === undefined) return;
      const newIndex = visualAIActiveItemIndex + direction;
      if (newIndex >= 0 && newIndex < visualAIPlaylistItems?.length) {
        dispatch(setVisualAIActiveItemIndex(newIndex));
      }
    },
    [visualAIActiveItemIndex, visualAIPlaylistItems, dispatch]
  );

  const handleClose = useCallback(() => {
    dispatch(setVisualAIActiveItemIndex(null));
    onClose && onClose();
  }, []);

  const renderLoadingState = () => (
    <div className="bg-black flex items-center justify-center w-full h-full animate-pulse ease-in-out flex-col text-primary-600 gap-1 rounded-xl">
      <VideoIcon size={36} />
      <img src={dataMindLogo} alt="DataMind Logo" />
      <div className="font-medium tracking-wider">Visual AI</div>
    </div>
  );

  return (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: cn('h-[360px] min-h-[360px] max-h-[360px]', className),
        title: 'h-[30px]',
        content: 'h-[calc(100%-30px)] rounded-lg flex justify-center p-0 bg-black',
      }}
      title={
        <div className="flex items-center justify-between w-full">
          <div>Evidence Video</div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 h-full">
              <BaseButton variant="ghost" size="xs" className="py-0 px-2" onClick={() => changeVideoIndex(1)}>
                <SkipBack />
              </BaseButton>
              <BaseButton variant="ghost" size="xs" className="py-0 px-2" onClick={() => changeVideoIndex(-1)}>
                <SkipForward />
              </BaseButton>
            </div>
            <BaseButton variant="ghost" size="xs" className="py-0 px-2 text-xl" onClick={handleClose}>
              <X size={14} />
            </BaseButton>
          </div>
        </div>
      }
    >
      {isLoadingResource ? (
        renderLoadingState()
      ) : isError ? (
        <div className="flex items-center justify-center w-full h-full flex-col gap-2 text-white">
          <span>Failed to load video</span>
          <button onClick={retry} className="text-primary-600 hover:underline text-sm">
            Try Again
          </button>
        </div>
      ) : (
        <DMVideoPlayer src={resource} />
      )}
    </BaseCard>
  );
};
